import type { Bill } from './lib/bills'

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

function when(b: Bill): string {
  if (b.days < 0) return `was due ${b.due}, ${-b.days} day${b.days === -1 ? '' : 's'} ago`
  if (b.days === 0) return `due today, ${b.due}`
  if (b.days === 1) return `due tomorrow, ${b.due}`
  return `due ${b.due}, in ${b.days} days`
}

/**
 * Card and loan bills coming due, from the latest statements. Urgency is an
 * icon and bold text, never hue; a bill AutoPay covers is shown lighter.
 */
export function BillsDue({
  bills,
  missing,
  reading,
  busy,
  onRead,
  onPaid,
}: {
  bills: Bill[]
  /** Latest statements read before due dates were captured. */
  missing: number
  /** How many of them have been re-read so far, while reading. */
  reading?: number
  busy: boolean
  onRead: () => void
  onPaid: (b: Bill) => void
}) {
  if (bills.length === 0 && missing === 0) return null
  return (
    <section className="bills" aria-label="Bills due">
      <p className="label">Bills due</p>
      {bills.map((b) => {
        const loan = b.kind !== 'credit'
        const auto = b.autopay === 'on'
        const urgent = !auto && b.days <= 3
        return (
          <div key={b.key} className={`bill${urgent ? ' urgent' : ''}${auto ? ' auto' : ''}`}>
            <span className="bill-icon" aria-hidden>{auto ? '↻' : urgent ? '!' : '•'}</span>
            <p>
              <b>{b.name}</b> {urgent ? <b>{when(b)}</b> : when(b)}
              {b.days < 0 && !auto && '. Check it was paid.'}
              <span className="muted">
                {' · '}
                {loan
                  ? b.minimum !== null && <span className="num">{money.format(b.minimum)} due</span>
                  : (
                    <>
                      {b.owed !== null && <>statement balance <span className="num">{money.format(b.owed)}</span></>}
                      {b.owed !== null && b.minimum !== null && ' · '}
                      {b.minimum !== null && <>minimum <span className="num">{money.format(b.minimum)}</span></>}
                    </>
                  )}
                {auto && ' · AutoPay on'}
                {b.autopay === 'off' && ' · no AutoPay'}
              </span>
            </p>
            <button className="quiet" title="Hides it until the next statement" onClick={() => onPaid(b)}>Paid</button>
          </div>
        )
      })}
      {missing > 0 && (
        <div className="bill auto">
          <span className="bill-icon" aria-hidden>?</span>
          <p>
            {reading !== undefined ? (
              <>Reading due dates… {reading} of {missing}</>
            ) : (
              <>
                The latest statement of {missing} card{missing === 1 ? '' : 's'} and loan{missing === 1 ? '' : 's'} was read before due dates were
                captured. Reading the due dates from {missing === 1 ? 'it' : 'them'} costs about $0.10 each on your Claude key.
              </>
            )}
          </p>
          <button disabled={busy || reading !== undefined} onClick={onRead}>Read due dates</button>
        </div>
      )}
    </section>
  )
}
